/**
 * In-memory registry of INSTALLED community designs, keyed by
 * `${widgetType}:${libraryRowId}` (`IDesignLibraryDB.widgetType` + `.id`).
 *
 * The live widget renderers resolve a widget's `options.designId` synchronously
 * via each catalog's `getXDesign(id)`, which only knows the shipped first-party
 * catalog. When a user installs a COMMUNITY design from the marketplace it lives
 * in Dexie (`installedDesigns`) — async — so the sync renderer can't see it.
 *
 * `useDesignLibrary` hydrates this registry (on load + on install/remove) so
 * `getXDesign` can fall back to it and a community design the user applied
 * renders live exactly like a first-party one. First-party ids always take
 * precedence (the catalog is checked first), so this only ever ADDS reach.
 *
 * CLAVE = widgetType + id de la FILA de la librería (no el `id` del payload):
 * es lo que `DesignStoreShell` persiste en `options.designId` (`fromRemote`
 * → `r.id`), y los payloads publicados comparten ids como `theme-chat` que
 * colisionarían entre instalaciones. Nadie consulta por el id del payload
 * (todos los `getXDesign` reciben `options.designId`), así que no hay alias.
 * Al registrar se normaliza `fonts` a `[]` (el backend lo permite ausente).
 */

import type {
  DesignWidgetType,
  SerializableDesign,
} from "./serializableDesign";

const REGISTRY = new Map<string, SerializableDesign>();

/** Lo mínimo de una fila de la librería (`IDesignLibraryDB`) que se registra. */
export interface InstalledDesignEntry {
  /** Id de la fila — el que persiste en `options.designId`. */
  id: string;
  /** Tipo de widget de la fila: forma parte de la clave (ver `keyOf`). */
  widgetType: DesignWidgetType;
  design: SerializableDesign;
}

/**
 * Clave = `${widgetType}:${idDeFila}`. Los ids first-party se repiten entre
 * catálogos (`dark` existe en chat y en timer) y la tabla `installedDesigns`
 * indexa sólo por id: sin el namespace, aplicar el `dark` de timer pisaría al
 * de chat en el registro. `options.designId` sigue siendo el id de fila; el
 * tipo lo aporta cada `getXDesign` al consultar.
 */
const keyOf = (widgetType: DesignWidgetType, id: string): string =>
  `${widgetType}:${id}`;

/** Copia del payload con los opcionales del backend normalizados (`fonts`). */
const normalizeDesign = (design: SerializableDesign): SerializableDesign => ({
  ...design,
  fonts: design.fonts ?? [],
});

/**
 * Versión del registro: sube en cada mutación y avisa a los suscriptores
 * (ver `useInstalledDesignsVersion`). Los widgets del editor resuelven
 * `options.designId` de forma sincrónica y la hidratación es async: sin esta
 * señal, una escena abierta antes de hidratar quedaría en legacy para siempre.
 */
let version = 0;
const listeners = new Set<() => void>();

const bumpVersion = (): void => {
  version += 1;
  for (const listener of listeners) {
    try {
      listener();
    } catch (err) {
      // Un suscriptor roto no debe dejar sin aviso a los demás.
      console.error("[installedRegistry] listener threw:", err);
    }
  }
};

/** Versión actual del registro (cambia con cada set/register/unregister). */
export function getInstalledDesignsVersion(): number {
  return version;
}

/** Suscribe a los cambios del registro; devuelve el unsubscribe. */
export function subscribeInstalledDesigns(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Replace the registry contents (called on library hydration). */
export function setInstalledDesigns(
  items: readonly InstalledDesignEntry[],
): void {
  REGISTRY.clear();
  for (const item of items) {
    if (item?.id && item.widgetType && item.design) {
      REGISTRY.set(
        keyOf(item.widgetType, item.id),
        normalizeDesign(item.design),
      );
    }
  }
  bumpVersion();
}

/** Add/replace one installed design (called on install). */
export function registerInstalledDesign(item: InstalledDesignEntry): void {
  if (item?.id && item.widgetType && item.design) {
    REGISTRY.set(
      keyOf(item.widgetType, item.id),
      normalizeDesign(item.design),
    );
  }
  bumpVersion();
}

/**
 * Drop one installed design by its library row id (called on remove). Sin
 * `widgetType` borra la fila en TODOS los tipos: el id de fila es único en la
 * tabla y `useDesignLibrary.removeDesign` sólo recibe el id.
 */
export function unregisterInstalledDesign(
  id: string,
  widgetType?: DesignWidgetType,
): void {
  if (widgetType) {
    REGISTRY.delete(keyOf(widgetType, id));
  } else {
    for (const key of [...REGISTRY.keys()]) {
      if (key.endsWith(`:${id}`)) REGISTRY.delete(key);
    }
  }
  bumpVersion();
}

/**
 * Resolve an installed design by id, optionally constrained to a widgetType
 * (so e.g. a chat design id can't accidentally satisfy a timer lookup).
 */
export function getInstalledDesign(
  id: string | undefined,
  widgetType?: SerializableDesign["widgetType"],
): SerializableDesign | undefined {
  if (!id) return undefined;
  if (widgetType) {
    const d = REGISTRY.get(keyOf(widgetType, id));
    // Defensa extra: el payload también debe declarar ese tipo.
    return d && d.widgetType === widgetType ? d : undefined;
  }
  // Sin tipo (ningún catálogo lo hace): primera fila con ese id.
  for (const [key, d] of REGISTRY) {
    if (key.endsWith(`:${id}`)) return d;
  }
  return undefined;
}
